"use client";

import Link from "@app/_components/Link";
import useTranslate from "@app/_hooks/useTranslate";
import { Button } from "@ui/button";
import { Heading } from "@ui/heading";
import { Paragraph } from "@ui/paragraph";
import { Loader2 } from "lucide-react";
import { usePathname } from "next/navigation";
import type { ComponentProps } from "react";
import useAuth from "../hooks/useAuth";
import AuthToggle from "./AuthToggle";

export default function AuthLayout({
  locale,
  children,
  ...props
}: ComponentProps<"nav"> & { locale: string }) {
  const t = useTranslate();
  const { user, authenticated, initializing } = useAuth();
  const pathname = usePathname();
  const welcomeName =
    user?.displayName?.trim() || user?.email?.split("@")[0] || "";
  const isSharedWishlist = pathname.includes("/account/wishlist/shared");

  if (isSharedWishlist) {
    return <div className="h-full">{children}</div>;
  }

  return (
    <>
      <nav
        className="top-13.25 flex w-full shrink-0 flex-col items-start self-start overflow-hidden max-lg:border-b max-lg:pb-4 lg:sticky lg:max-w-xs"
        {...props}
      >
        {initializing ? (
          <div className="flex p-4 lg:p-10">
            <Loader2 className="size-6 animate-spin" />
          </div>
        ) : authenticated ? (
          <>
            <Heading as="h4" className="p-4 max-lg:text-2xl lg:p-10">
              {welcomeName ? `${t("welcome")}, ${welcomeName}` : t("welcome")}
            </Heading>
            <ul className="scrollbar-none grid w-full grid-flow-col justify-start gap-x-4 overflow-x-auto px-4 pb-4 lg:grid-flow-row lg:px-10 lg:pb-10">
              <li>
                <Link
                  data-active={pathname.endsWith("/account/orders")}
                  className="block py-1 whitespace-nowrap underline-offset-4 data-active:underline"
                  locale={locale}
                  href="/account/orders"
                >
                  {t("OrderTitle")}
                </Link>
              </li>
              <li>
                <Link
                  data-active={pathname.endsWith("/account/wishlist")}
                  className="block py-1 whitespace-nowrap underline-offset-4 data-active:underline"
                  locale={locale}
                  href="/account/wishlist"
                >
                  {t("wishlist")}
                </Link>
              </li>
              <li>
                <Link
                  data-active={pathname.endsWith("/account/notifications")}
                  className="block py-1 whitespace-nowrap underline-offset-4 data-active:underline"
                  locale={locale}
                  href="/account/notifications"
                >
                  {t("notifications")}
                </Link>
              </li>
              <li>
                <Link
                  data-active={pathname.endsWith("/account/settings")}
                  className="block py-1 whitespace-nowrap underline-offset-4 data-active:underline"
                  locale={locale}
                  href="/account/settings"
                >
                  {t("accountSettings")}
                </Link>
              </li>
              <AuthToggle variant="link" className="block p-0 lg:mt-4" />
            </ul>
          </>
        ) : (
          <div className="flex flex-col gap-y-4 p-4 lg:w-xs lg:p-6">
            <Paragraph>{t("saveYourWishlist")}</Paragraph>
            <AuthToggle />
            <Button asChild variant="outline">
              <Link href="/register" locale={locale}>
                {t("createAccount")}
              </Link>
            </Button>
          </div>
        )}
      </nav>
      <div className="h-full lg:border-l">{children}</div>
    </>
  );
}
